import { H7, useTheme, useWindowProperties } from '@qred/components-library';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { ConfirmationCard } from '~/components/shared/ConfirmationCard/ConfirmationCard';
import Header from '~/components/shared/Header/Header';
import constVars from '~/constants/constVars';
import { getHomePageByMarket } from '~/constants/markets';
import { ApplicationStatus, WSAutoApprovalStatus, WSEventType } from '~/enums';
import {
  wsAuthConfirmationSchema,
  wsOfferMessageSchema,
} from '~/schemas/DTO/websocket';
import { logToSentry } from '~/helpers/loggers.helper';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useTranslate from '~/hooks/useTranslate';
import { RootState } from '~/store';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import { accessToken } from '~/store/apis/utils/accessToken';
import {
  clearLoanApplicationState,
  updateApplicationStatus,
} from '~/store/slices/loanApplication.slice';
import { ApiStatus } from '~/store/types/sharedTypes';
import {
  GoldenRatioContainer,
  StandardPageContainer,
} from '~/styles/BaseStyledComponents';
import { ProcessingCard } from './components/ProcessingCard';

export const ConfirmationPage = () => {
  const t = useTranslate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const history = useHistory();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const websocket = useRef<WebSocket | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const {
    loanApplication: { applicationStatus, form, apiStatus },
    intl: { market, language },
  } = useSelector((state: RootState) => state);
  const { isMobile } = useWindowProperties();

  const applicationUuid = form.applicationUuid;

  const isApproved = applicationStatus === ApplicationStatus.OfferApproved;
  const isNotApproved =
    applicationStatus === ApplicationStatus.OfferNotApproved;
  const isProcessing =
    applicationStatus === ApplicationStatus.ApplicationUnderReview ||
    applicationStatus === ApplicationStatus.ApplicationFinished;

  const onClickGoToWebsite = () => {
    window.location.assign(getHomePageByMarket(market, language));
  };

  const onClickViewOffer = () => {
    const savedApplicationUuid = applicationUuid;
    dispatch(clearLoanApplicationState());
    history.push(`/${constVars.ROUTE_LOAN_OFFER}/${savedApplicationUuid}`);
  };

  useEffect(() => {
    if (
      apiStatus.patch === ApiStatus.Completed &&
      applicationStatus === ApplicationStatus.ApplicationFinished
    ) {
      dispatch(
        updateApplicationStatus(ApplicationStatus.ApplicationUnderReview)
      );
    }
  }, [apiStatus.patch, applicationStatus]);

  useEffect(() => {
    if (applicationStatus === ApplicationStatus.ApplicationUnderReview) {
      const cancelTimeout = () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };

      const dispatchNotApproved = () => {
        dispatch(updateApplicationStatus(ApplicationStatus.OfferNotApproved));
        dispatch(pushToGtmLoanAction({ actionName: 'not_auto_approved' }));
      };

      const dispatchApproved = () => {
        dispatch(updateApplicationStatus(ApplicationStatus.OfferApproved));
        dispatch(pushToGtmLoanAction({ actionName: 'auto_approved' }));
      };

      websocket.current = new WebSocket(
        import.meta.env.VITE_WEBSOCKET_ONBOARDING_URL
      );

      websocket.current.onopen = async () => {
        setErrorMessage(null);
        const userData = {
          action: 'authorization',
          accessToken: await accessToken.get(),
          callbackId: applicationUuid,
          countryCode: market.toLowerCase(),
          eventType: WSEventType.LoanAutoApproval,
        };
        if (websocket.current) {
          websocket.current.send(JSON.stringify(userData));
        }
      };

      websocket.current.onerror = (error: Event) => {
        logToSentry(error, 'websocket onerror', {});
        setErrorMessage(t('Onboarding.Disconnected') as string);
      };

      websocket.current.onmessage = ({ data }: MessageEvent) => {
        try {
          const parsed = JSON.parse(data);

          const authResult = wsAuthConfirmationSchema.safeParse(parsed);
          const offerResult = wsOfferMessageSchema.safeParse(parsed);

          if (authResult.success) {
            timeoutRef.current = setTimeout(
              dispatchNotApproved,
              Number(import.meta.env.VITE_WS_NOT_APPROVED_TIMEOUT_MS)
            );
          } else if (offerResult.success) {
            setErrorMessage(null);
            cancelTimeout();
            offerResult.data.message.status === WSAutoApprovalStatus.Approved
              ? dispatchApproved()
              : dispatchNotApproved();
          }
        } catch (error) {
          logToSentry(error, 'websocket try/catch error', {});
          setErrorMessage(t('ApiFailureError.ErrorMessageTitle') as string);
          console.error(error);
        }
      };
    }
  }, [applicationStatus]);

  useEffect(() => {
    return () => {
      if (websocket?.current) {
        websocket.current.close();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <StandardPageContainer
      style={{
        backgroundColor: theme.fn.themeColor('Page/Option_2_(Green)'),
      }}
    >
      <Header />
      <GoldenRatioContainer $grow={isMobile && !isProcessing}>
        {!errorMessage ? (
          <>
            {isProcessing && <ProcessingCard />}

            {isApproved && (
              <ConfirmationCard
                header={t('Onboarding.Congratulations') as string}
                text={
                  t('Onboarding.PreApprovedApplicationDescription') as string
                }
                buttonText={t('Onboarding.ViewOffer') as string}
                onButtonClick={onClickViewOffer}
                headerImgSrc="/congratulations_qrayon.png"
              />
            )}

            {isNotApproved && (
              <ConfirmationCard
                header={t('Onboarding.ApplicationSubmitted') as string}
                text={
                  t(
                    'Onboarding.ApplicationSubmittedDescriptionNotAutoApproved'
                  ) as string
                }
                buttonText={t('Onboarding.GoToQredWebsite') as string}
                onButtonClick={onClickGoToWebsite}
              />
            )}
          </>
        ) : (
          <H7>{errorMessage}</H7>
        )}
      </GoldenRatioContainer>
    </StandardPageContainer>
  );
};
